import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/AuthProvider";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  plan: string;
}

async function fetchOrganization(userId: string): Promise<Organization | null> {
  const { data: member, error: memberError } = await supabase
    .from("org_members")
    .select("organization_id, organizations(id, name, slug, logo_url, plan)")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (memberError) throw memberError;

  const joinedOrg = (member?.organizations as unknown as Organization | null) ?? null;
  if (joinedOrg?.id) return joinedOrg;

  const { data: orgId, error: orgIdError } = await supabase.rpc("get_my_org_id");
  if (orgIdError) throw orgIdError;
  if (!orgId) return null;

  const { data: org, error: orgError } = await supabase
    .from("organizations")
    .select("id, name, slug, logo_url, plan")
    .eq("id", orgId)
    .maybeSingle();
  if (orgError) throw orgError;
  return (org as Organization | null) ?? null;
}

export function useOrganization() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["organization", user?.id ?? null],
    enabled: Boolean(user?.id),
    queryFn: () => fetchOrganization(user!.id),
  });

  return {
    ...query,
    organization: query.data ?? null,
    organizationId: query.data?.id ?? null,
  };
}
